/**
 * Herbereken's progress dialog: what the recompute is doing now, step by step,
 * while the page keeps the current snapshot. It opens when this page starts a
 * recompute, and on a click of the busy button for any running one (the daily
 * one, someone else's); never by itself on page load. "Verbergen" closes it
 * while the recompute goes on. A success says which Odoo-stand the page shows
 * now and closes by itself; a success whose new figures could not be loaded
 * says so and offers to try again; a failure marks the step it stopped in.
 * Those two stay until they are closed. The controller keeps what it shows
 * (`progressView()`).
 */

import { Circle, CircleCheck, CircleX, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AppJob, AppSnapshotSummary, JobProgress } from "@/lib/api";
import { formatPercent, formatTime } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { nl } from "@/lib/i18n/locales/nl";
import { cn } from "@/lib/utils";

import type { RecomputeState } from "./contract";
import { jobErrorText } from "./job-errors";
import {
  type RecomputeController,
  type RecomputeProgressView,
  useRecomputeProgress,
  useRecomputeState,
} from "./recompute";
import {
  failedFraction,
  formatElapsed,
  progressFraction,
  recomputeOutcome,
  type RecomputeStage,
  type RecomputeStep,
  recomputeSteps,
  stageFraction,
  type StepStatus,
  wholePercent,
} from "./recompute-progress";

type TableKey = keyof typeof nl.apps.progress.tables;
type PartKey = keyof typeof nl.apps.progress.parts;

/** An Odoo read the backend names differently from its label. */
const TABLE_ALIASES: Readonly<Record<string, TableKey>> = {
  extra_sale_order_log: "sale_order_log",
};

/** How often the elapsed time moves on. */
const TICK_MS = 1_000;

/** The label of what a step works on: the table read, the part built; the step's own label for one it does not know. */
export function itemLabel(stage: RecomputeStage, item: string | null): string | null {
  if (item === null) {
    return null;
  }
  if (stage === "odoo_read") {
    const key = TABLE_ALIASES[item] ?? item;
    if (Object.hasOwn(nl.apps.progress.tables, key)) {
      return i18n.t(`apps.progress.tables.${key as TableKey}`);
    }
  }
  if (stage === "build" && Object.hasOwn(nl.apps.progress.parts, item)) {
    return i18n.t(`apps.progress.parts.${item as PartKey}`);
  }
  return i18n.t(`apps.progress.steps.${stage}`);
}

/**
 * The active step's detail: the table and its records while Odoo is read
 * ("Orderlijnen, 4.200 van 31.000 records"), the part while the pages are
 * built; nothing while the data is checked or stored.
 */
export function stepDetail(progress: JobProgress): string | null {
  const stage = progress.stage;
  if (stage !== "odoo_read" && stage !== "build") {
    return null;
  }
  const label = itemLabel(stage, progress.item);
  if (label === null || stage === "build" || progress.item_done === null) {
    return label;
  }
  return progress.item_total === null
    ? i18n.t("apps.progress.readCount", { table: label, count: progress.item_done })
    : i18n.t("apps.progress.read", {
        table: label,
        done: progress.item_done,
        count: progress.item_total,
      });
}

/** The clock, moving on every second while `ticking`. */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [ticking]);
  return now;
}

/** What a failed recompute says, with whether the page still shows the previous data. */
type JobErrorText = (job: Pick<AppJob, "error">, previousKept: boolean) => string;

interface RecomputeDialogProps {
  recompute: RecomputeController;
  /** The snapshot summary: whether the page has data, and its Odoo-stand. */
  summary: AppSnapshotSummary | undefined;
  /** The app's wording of a failure: Bezettingsgraad's by default. */
  errorText?: JobErrorText;
}

/** The dialog, mounted once by the host; it shows while the controller says it is open. */
export function RecomputeDialog({
  recompute,
  summary,
  errorText = jobErrorText,
}: RecomputeDialogProps) {
  const { t } = useTranslation();
  const view = useRecomputeProgress(recompute);
  const state = useRecomputeState(recompute);
  return (
    <Dialog
      open={view.open}
      onOpenChange={(open) => {
        if (!open) {
          recompute.hideProgress();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("apps.progress.title")}</DialogTitle>
          <DialogDescription>
            {summary?.snapshot ? t("apps.progress.lead") : t("apps.progress.leadFirst")}
          </DialogDescription>
        </DialogHeader>
        <RecomputeProgressPanel
          view={view}
          state={state}
          summary={summary}
          onHide={recompute.hideProgress}
          onRetry={recompute.retryLoad}
          errorText={errorText}
        />
      </DialogContent>
    </Dialog>
  );
}

interface RecomputeProgressPanelProps {
  view: RecomputeProgressView;
  state: RecomputeState;
  summary: AppSnapshotSummary | undefined;
  /** "Verbergen" while it runs, "Sluiten" once it ended. */
  onHide: () => void;
  /** "Opnieuw proberen" after a success whose new figures could not be loaded. */
  onRetry: () => void;
  /** The app's wording of a failure: Bezettingsgraad's by default. */
  errorText?: JobErrorText;
}

/** The dialog's body: the total bar, the four steps, how it ended, and the buttons. */
export function RecomputeProgressPanel({
  view,
  state,
  summary,
  onHide,
  onRetry,
  errorText = jobErrorText,
}: RecomputeProgressPanelProps) {
  const { t } = useTranslation();
  const { job, progress } = view;
  const outcome = recomputeOutcome(job);
  const running = outcome === "running";
  const now = useNow(running);

  const startedAt = job?.started_at ?? (state.kind === "running" ? state.startedAt : null);
  const elapsedMs =
    !running && job?.duration_ms != null
      ? job.duration_ms
      : startedAt === null
        ? 0
        : now - Date.parse(startedAt);
  const elapsed = formatElapsed(elapsedMs);
  const fraction =
    outcome === "complete"
      ? 1
      : outcome === "failed"
        ? failedFraction(job?.error ?? null, progress)
        : progressFraction(progress);
  const steps = recomputeSteps(outcome, progress, job?.error ?? null);
  const active = steps.find((step) => step.status === "active");
  const readAt = summary?.snapshot?.odoo_read_at ?? null;
  const notLoaded = outcome === "complete" && view.loadFailed;

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-4 text-sm tabular-nums">
          <span className="font-semibold">
            {fraction !== null && outcome !== "failed"
              ? formatPercent(wholePercent(fraction) / 100)
              : null}
          </span>
          <span className="text-muted">
            {outcome === "complete"
              ? t("apps.progress.finished", { time: elapsed })
              : outcome === "failed"
                ? t("apps.progress.stopped", { time: elapsed })
                : t("apps.progress.running", { time: elapsed })}
          </span>
        </div>
        <ProgressBar
          fraction={fraction}
          failed={outcome === "failed"}
          label={t("apps.progress.label")}
        />
      </div>

      <ol className="space-y-3">
        {steps.map((step) => (
          <StepRow key={step.stage} step={step} />
        ))}
      </ol>
      {/* The active step and what it works on, for a screen reader; the record counts stay out of it. */}
      <p aria-live="polite" className="sr-only">
        {active
          ? [
              t(`apps.progress.steps.${active.stage}`),
              itemLabel(active.stage, active.progress?.item ?? null),
            ]
              .filter((part) => part !== null)
              .join(", ")
          : ""}
      </p>

      {outcome === "complete" && view.settled && readAt !== null && (
        <p role="status" className="text-sm">
          {t("apps.progress.done", { time: formatTime(readAt) })}
        </p>
      )}
      {notLoaded && (
        <p role="alert" className="text-sm">
          {t("apps.progress.notLoaded")}
        </p>
      )}
      {outcome === "failed" && job && (
        <p role="alert" className="text-destructive text-sm">
          {errorText(job, Boolean(summary?.snapshot))}
        </p>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onHide}>
          {running ? t("apps.progress.hide") : t("common.close")}
        </Button>
        {notLoaded && <Button onClick={onRetry}>{t("common.tryAgain")}</Button>}
      </DialogFooter>
    </>
  );
}

interface StepRowProps {
  step: RecomputeStep;
}

/** One step: its mark, its label, and while it runs what it works on and a small bar. */
function StepRow({ step }: StepRowProps) {
  const { t } = useTranslation();
  const detail = step.status === "active" && step.progress ? stepDetail(step.progress) : null;
  return (
    <li className="flex items-start gap-3" data-step={step.stage} data-status={step.status}>
      <StepMark status={step.status} />
      <div className="min-w-0 flex-1 space-y-1">
        <p
          className={cn(
            "text-sm",
            step.status === "pending" && "text-muted",
            step.status === "active" && "text-foreground font-semibold",
            step.status === "failed" && "text-destructive font-semibold",
          )}
        >
          {t(`apps.progress.steps.${step.stage}`)}
          <span className="sr-only">, {t(`apps.progress.status.${step.status}`)}</span>
        </p>
        {step.status === "active" && (
          <>
            {detail !== null && <p className="text-muted text-xs tabular-nums">{detail}</p>}
            {step.progress && <ProgressBar fraction={stageFraction(step.progress)} small />}
          </>
        )}
      </div>
    </li>
  );
}

function StepMark({ status }: { status: StepStatus }) {
  const className = "mt-0.5 size-4 shrink-0";
  if (status === "active") {
    return <Loader2 aria-hidden="true" className={cn(className, "text-primary animate-spin")} />;
  }
  if (status === "done") {
    return <CircleCheck aria-hidden="true" className={cn(className, "text-primary")} />;
  }
  if (status === "failed") {
    return <CircleX aria-hidden="true" className={cn(className, "text-destructive")} />;
  }
  return <Circle aria-hidden="true" className={cn(className, "text-muted")} />;
}

interface ProgressBarProps {
  /** 0 to 1; null shows the empty track. */
  fraction: number | null;
  /** Where a failed recompute stopped, in the error colour. */
  failed?: boolean;
  /** The small bar under the active step. */
  small?: boolean;
  /** The name a screen reader gives the bar; a small bar stays out of its way. */
  label?: string;
}

/** A square bar: De Ceuster blauw on the light grey track. */
function ProgressBar({ fraction, failed = false, small = false, label }: ProgressBarProps) {
  const height = small ? "h-1" : "h-2";
  if (fraction === null) {
    return <div aria-hidden="true" className={cn("bg-chart-3 w-full", height)} />;
  }
  return (
    <progress
      max={100}
      value={wholePercent(fraction)}
      aria-label={label}
      aria-hidden={label === undefined ? true : undefined}
      className={cn(
        "bg-chart-3 block w-full appearance-none border-0 [&::-webkit-progress-bar]:bg-transparent",
        height,
        failed
          ? "[&::-moz-progress-bar]:bg-destructive [&::-webkit-progress-value]:bg-destructive"
          : "[&::-moz-progress-bar]:bg-primary [&::-webkit-progress-value]:bg-primary",
      )}
    />
  );
}
